/**
 * JoinTeamPage — Team workspaces (V2 additive).
 *
 * Invite-link landing at /team/join/:token. The route sits behind the app's
 * auth gate, so an invitee without an account signs in first and lands back
 * here. Accepting creates the membership (seat-capped server-side), switches
 * the active workspace to the team, and routes home.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { orgService } from '@/api/orgService';

export function JoinTeamPage() {
  const { t } = useTranslation('v2');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const refreshUser = useUserStore((s) => s.refreshUser);
  const setActiveOrg = useWorkspaceStore((s) => s.setActiveOrg);

  const [state, setState] = useState<'loading' | 'joined' | 'error'>('loading');
  const [orgName, setOrgName] = useState('');
  const [errorKey, setErrorKey] = useState('team.joinErrorGeneric');
  const attempted = useRef(false);

  useEffect(() => {
    if (!userId || !token || attempted.current) return;
    attempted.current = true;
    (async () => {
      try {
        const res = await orgService.acceptInvite(userId, token);
        setOrgName(res.org_name || '');
        setActiveOrg(res.org_id);
        await refreshUser();
        setState('joined');
      } catch (e) {
        const code = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
        if (code === 'invite_expired') setErrorKey('team.joinErrorExpired');
        else if (code === 'invite_not_found') setErrorKey('team.joinErrorNotFound');
        else if (code === 'no_seats_available') setErrorKey('team.joinErrorNoSeats');
        else if (code === 'org_expired') setErrorKey('team.joinErrorOrgExpired');
        setState('error');
      }
    })();
  }, [userId, token, refreshUser, setActiveOrg]);

  return (
    <div className="px-4 py-20 flex flex-col items-center text-center animate-fade-in">
      {state === 'loading' ? (
        <>
          <Loader2 className="w-10 h-10 text-primary animate-spin mb-5" />
          <p className="text-sm text-text-secondary">{t('team.joining')}</p>
        </>
      ) : state === 'joined' ? (
        <>
          <span className="w-16 h-16 rounded-2xl bg-emerald-500/15 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </span>
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            {t('team.joinedTitle', { name: orgName || t('team.teamWorkspace') })}
          </h1>
          <p className="text-sm text-text-secondary max-w-md mb-6">{t('team.joinedDescription')}</p>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-white font-semibold"
          >
            <Users className="w-4 h-4" /> {t('team.goToWorkspace')}
          </button>
        </>
      ) : (
        <>
          <span className="w-16 h-16 rounded-2xl bg-red-500/15 flex items-center justify-center mb-5">
            <XCircle className="w-8 h-8 text-red-400" />
          </span>
          <h1 className="text-2xl font-bold text-text-primary mb-2">{t('team.joinErrorTitle')}</h1>
          <p className="text-sm text-text-secondary max-w-md mb-6">{t(errorKey)}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-xl bg-surface-light text-text-primary font-semibold"
          >
            {t('team.backHome')}
          </button>
        </>
      )}
    </div>
  );
}
